import { Color, Flex, Modal, SectionMessage, Spacing, Text } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useResolveTaxAndRegistrationNoLabel } from '../../../../../../hooks/useResolveTaxAndRegistrationNoLabel'
import { useInvoiceComplianceMode } from '../../../../hooks/useInvoiceComplianceMode'
import { MissingEInvoiceDetails } from '../../../../types/missingEInvoiceDetails'
import * as Styled from './styles'
import { getMissingCompanyDetailsListSubItems } from './utils/getMissingCompanyDetailsListSubItems'
import { getMissingCustomerDetailsListSubItems } from './utils/getMissingCustomerDetailsListSubItems'

type SendEInvoiceRequirementsModalBodyProps = {
  missingEInvoiceDetails?: MissingEInvoiceDetails
}

export const SendEInvoiceRequirementsModalBody = ({
  missingEInvoiceDetails,
}: SendEInvoiceRequirementsModalBodyProps): ReactElement | null => {
  const { t } = useTranslation()
  const resolvedTaxAndRegistrationNoLabel = useResolveTaxAndRegistrationNoLabel()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()

  const { isMissingCompanyDetails, isMissingCustomerDetails, companyDetails, customerDetails } =
    missingEInvoiceDetails || {}

  const sendEInvoiceMissingRequirementsList = useMemo(
    () =>
      [
        {
          isMissing: isMissingCompanyDetails,
          label: t('external_invoices.send_e_invoice_requirements.modal.section_message.list.company_details.item'),
          subItems: getMissingCompanyDetailsListSubItems({
            isFrenchCompliance: isFrenchComplianceMode,
            missingCompanyDetails: companyDetails,
            resolvedTaxAndRegistrationNoLabel,
            t,
          }),
        },
        {
          isMissing: isMissingCustomerDetails,
          label: t('external_invoices.send_e_invoice_requirements.modal.section_message.list.customer_details.item'),
          subItems: getMissingCustomerDetailsListSubItems({ missingCustomerDetails: customerDetails, t }),
        },
      ].filter((requirement) => requirement.isMissing),
    [
      companyDetails,
      customerDetails,
      isFrenchComplianceMode,
      isMissingCompanyDetails,
      isMissingCustomerDetails,
      resolvedTaxAndRegistrationNoLabel,
      t,
    ],
  )

  const sendEInvoiceSolutionsList = useMemo(
    () => [
      t('external_invoices.send_e_invoice_requirements.modal.body.list.item.try_another_delivery_method'),
      t('external_invoices.send_e_invoice_requirements.modal.body.list.item.cancel_&_create_new_invoice'),
    ],
    [t],
  )

  if (!missingEInvoiceDetails) {
    return null
  }

  return (
    <Modal.Body>
      <Flex flexDirection="column" gap={Spacing.S16}>
        <SectionMessage icon="info" showIcon variant="error">
          <Text color={Color.Red800} variant="bodyLG" weight="bold">
            {t('external_invoices.send_e_invoice_requirements.modal.section_message.list.title')}
          </Text>
          <Styled.List>
            {sendEInvoiceMissingRequirementsList.map(({ label, subItems }, index) => (
              <Styled.ListItem key={index}>
                <Text color={Color.Red800} variant="bodyLG">
                  {label}
                </Text>
                <Styled.SubList>
                  {subItems.map((text, index) => (
                    <Styled.ListItem key={index}>
                      <Text color={Color.Red800} variant="bodyLG">
                        {text}
                      </Text>
                    </Styled.ListItem>
                  ))}
                </Styled.SubList>
              </Styled.ListItem>
            ))}
          </Styled.List>
        </SectionMessage>

        <Flex flexDirection="column">
          <Text>{t('external_invoices.send_e_invoice_requirements.modal.body.list.title')}</Text>
          <Styled.List>
            {sendEInvoiceSolutionsList.map((text, index) => (
              <Styled.ListItem key={index}>
                <Text>{text}</Text>
              </Styled.ListItem>
            ))}
          </Styled.List>
        </Flex>
      </Flex>
    </Modal.Body>
  )
}
